import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { setToken } from '../services/tokenService';
import { Heart, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SponsorActivationPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, valid: false, error: '', invite: null });
  const [form, setForm] = useState({ password: '', confirm: '', contact_name: '', accept_terms: false });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/sponsor/activation/${token}`);
        setState({ loading: false, valid: true, error: '', invite: res.data });
        setForm((f) => ({ ...f, contact_name: res.data.organisation || '' }));
      } catch (e) {
        setState({ loading: false, valid: false, error: e?.response?.data?.detail || 'Invitation invalide', invite: null });
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (form.password.length < 8) { setSubmitError('Le mot de passe doit contenir au moins 8 caractères.'); return; }
    if (form.password !== form.confirm) { setSubmitError('Les mots de passe ne correspondent pas.'); return; }
    if (!form.accept_terms) { setSubmitError('Vous devez accepter les conditions d\'utilisation.'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/sponsor/activation/${token}`, {
        password: form.password, contact_name: form.contact_name, accept_terms: form.accept_terms,
      });
      setToken(res.data.access_token);
      navigate('/espace-sponsor');
    } catch (e) {
      setSubmitError(e?.response?.data?.detail || 'Erreur lors de l\'activation.');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4" data-testid="sponsor-activation-page">
      <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-2xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-emerald-500/20 flex items-center justify-center">
            <Heart className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-white text-xl font-bold">Activation Espace Sponsor</h1>
            <p className="text-slate-400 text-sm">OBJECTIF SCOP OUTREMER</p>
          </div>
        </div>

        {state.loading && (
          <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /> Vérification…</div>
        )}

        {!state.loading && !state.valid && (
          <div className="flex items-start gap-3 text-amber-300 bg-amber-500/10 rounded-lg p-4" data-testid="activation-error">
            <AlertTriangle className="w-5 h-5 mt-0.5" />
            <div><p className="font-semibold">Invitation indisponible</p><p className="text-sm text-amber-200/80">{state.error}</p></div>
          </div>
        )}

        {!state.loading && state.valid && (
          <form onSubmit={submit} className="space-y-4" data-testid="activation-form">
            <p className="text-slate-300 text-sm">
              Bienvenue <strong className="text-white">{state.invite?.organisation}</strong> — définissez votre mot de passe pour accéder à votre espace.
            </p>
            <div className="space-y-1.5">
              <label className="text-slate-400 text-sm">Nom du représentant</label>
              <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white" data-testid="activation-name" />
            </div>
            <div className="space-y-1.5">
              <label className="text-slate-400 text-sm">Mot de passe</label>
              <input type="password" autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white" data-testid="activation-password" />
            </div>
            <div className="space-y-1.5">
              <label className="text-slate-400 text-sm">Confirmer le mot de passe</label>
              <input type="password" autoComplete="new-password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white" data-testid="activation-confirm" />
            </div>
            <label className="flex items-start gap-2 text-slate-300 text-sm cursor-pointer">
              <input type="checkbox" checked={form.accept_terms} onChange={(e) => setForm({ ...form, accept_terms: e.target.checked })}
                className="mt-1" data-testid="activation-terms" />
              <span>J'accepte les conditions d'utilisation de l'espace sponsor.</span>
            </label>
            {submitError && <p className="text-red-400 text-sm" data-testid="activation-submit-error">{submitError}</p>}
            <button type="submit" disabled={submitting}
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold rounded-lg py-2.5 flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="activation-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Activer mon espace
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
